import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSofteamboardStore } from '../../store/softeamboardStore';
import { needsApi } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import { isFreshNeed } from '../../utils/needUtils';
import { 
  Building, Users, MapPin, GraduationCap, Star, TrendingUp, 
  Briefcase, Zap, Brain, AlertCircle, MoreVertical, ChevronDown, ChevronUp,
  Calendar, User, ChevronRight, Eye, Edit3, RotateCcw, Info, Search, Send, CheckCircle, XCircle, Snowflake, Trash2, Sparkles
} from 'lucide-react';

// Helper to build initials (max 2 letters)
const getInitials = (fullName = '') =>
  fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('');

const KanbanCard = ({ need, onClick }) => {
  const {
    consultants,
    getCandidatesForNeed,
    loadCandidatesForNeed, // ✅ PERFORMANCE FIX: Add lazy loading function
    getStepsForCandidate,
    loadStepsForCandidate
    // ✅ PERFORMANCE: Removed candidateSteps subscription - this was causing ALL cards
    // to re-render whenever ANY step changed for ANY candidate in ANY need (100+ cards re-rendering)
    // Now only using getStepsForCandidate() which accesses cached data without subscribing to entire object
  } = useSofteamboardStore();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [showJDAnalysis, setShowJDAnalysis] = useState(false);
  const [showCandidatesDetail, setShowCandidatesDetail] = useState(false);
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const [candidatesFetched, setCandidatesFetched] = useState(false);
  const [needData, setNeedData] = useState(need);
  // ✅ NEW: Local version to force recompute of step-based memos
  const [stepsVersion, setStepsVersion] = useState(0);
  
  // ✅ FIX: Add refs to prevent multiple API calls
  const fetchingRef = useRef(false);
  const lastFetchedNeedId = useRef(null);

  // ✅ PERFORMANCE PHASE 2: Cache for getMostAdvancedStep results
  // Key format: "candidateId_stepCount_firstStepId"

  // ✅ FIX: Get candidates from store FIRST, before any other useMemo hooks
  const candidates = getCandidatesForNeed(need?.id);

  // ✅ PERFORMANCE FIX: Lazy load candidates when need is first viewed
  useEffect(() => {
    if (!need?.id) return;

    // Only load if we don't have candidates yet and haven't already fetched for this need
    if (candidates.length === 0 && lastFetchedNeedId.current !== need.id && !fetchingRef.current) {
      fetchingRef.current = true;
      lastFetchedNeedId.current = need.id;

      loadCandidatesForNeed(need.id)
        .catch(err => {
          console.error(`Failed to load candidates for need ${need.id}:`, err);
          setCandidatesError(err.message);
        })
        .finally(() => {
          fetchingRef.current = false;
          setCandidatesFetched(true);
        });
    }
  }, [need?.id, candidates.length, loadCandidatesForNeed]);

  // ✅ SIMPLIFY: Use candidates directly from store
  const displayCandidates = useMemo(() => {
    return candidates.filter(candidate => candidate && candidate.id);
  }, [candidates]);

  // ✅ REMOVED: Eager step loading - now steps are bulk loaded with candidates on page load
  // This eliminates 1000+ individual step API calls on board load
  // Steps are now pre-loaded in the store's loadNeeds function via bulkLoadSteps

  // ✅ NEW: Step configuration with colors matching the request
  const stepConfig = useMemo(() => ({
    'CANDIDATE_IDENTIFIED': {
      label: 'I',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      bgColor: 'bg-blue-50',
      fullLabel: 'Identified'
    },
    'PUSH': {
      label: 'P',
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      bgColor: 'bg-indigo-50',
      fullLabel: 'Push'
    },
    'CANDIDATE_SENT': {
      label: 'S',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      bgColor: 'bg-yellow-50',
      fullLabel: 'Sent'
    },
    'CANDIDATE_INTERVIEW': {
      label: 'IW',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      bgColor: 'bg-purple-50',
      fullLabel: 'Interview'
    },
    'GO_NOGO': {
      label: 'GNG',
      color: 'bg-green-100 text-green-800 border-green-200',
      bgColor: 'bg-green-50',
      fullLabel: 'Go/No-Go'
    },
    'FROZEN': {
      label: 'F',
      color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      bgColor: 'bg-cyan-50',
      fullLabel: 'Frozen'
    },
    'GO_NOGO_GO': {
      label: 'GO',
      color: 'bg-green-100 text-green-800 border-green-200',
      bgColor: 'bg-green-50',
      fullLabel: 'Go Decision'
    },
    'GO_NOGO_NO_GO': {
      label: 'NO',
      color: 'bg-red-100 text-red-800 border-red-200',
      bgColor: 'bg-red-50',
      fullLabel: 'No-Go Decision'
    }
  }), []);

  // ✅ FIX: Add helper function to get the most advanced step in workflow
  const getMostAdvancedStep = useCallback((steps, candidateId = null) => {
    if (!steps || steps.length === 0) {
      return { type: 'NO_CANDIDATE', date: new Date().toISOString() };
    }


    // ✅ FIX: Define workflow progression order (least advanced first, most advanced last)
    const workflowProgression = [
      'NO_CANDIDATE',
      'CANDIDATE_IDENTIFIED',
      'CANDIDATE_SENT',
      'CANDIDATE_INTERVIEW',
      'GO_NOGO',
      'SALE',
      'CANCELED',
      'DELETED'
    ];

    // ✅ FIX: Find the most advanced step in the workflow
    let mostAdvancedStep = null;
    let highestIndex = -1;

    steps.forEach(step => {
      // ✅ FIXED: Don't skip NO_GO decisions - they should be considered as advanced steps
      // The display logic will handle showing them as GO_NOGO_NO_GO with red NO tag
      const stepIndex = workflowProgression.indexOf(step.type);
      if (stepIndex > highestIndex) {
        highestIndex = stepIndex;
        mostAdvancedStep = step;
      }
    });

    const result = mostAdvancedStep || { type: 'NO_CANDIDATE', date: new Date().toISOString() };


    return result;
  }, []);

  // ✅ FIXED: Calculate candidate step distribution AFTER candidates is declared
  const candidateStepDistribution = useMemo(() => {
    if (!need?.id || !candidates.length) {
      return {};
    }

    const distribution = {
      'CANDIDATE_IDENTIFIED': 0,
      'CANDIDATE_SENT': 0,
      'CANDIDATE_INTERVIEW': 0,
      'GO_NOGO_GO': 0,
      'GO_NOGO_NO_GO': 0,
      'FROZEN': 0,
      'PUSH': 0
    };

    candidates.forEach(candidate => {
      if (candidate && candidate.id) {
        // Get steps for this candidate
        const steps = getStepsForCandidate(need.id, candidate.id);
        
        if (steps && steps.length > 0) {
          // ✅ FIX: Use the most advanced step instead of the latest by date
          // ✅ PHASE 2: Pass candidateId for caching
          const mostAdvancedStep = getMostAdvancedStep(steps, candidate.id);
          const stepType = mostAdvancedStep.type;
          
          if (stepType === 'GO_NOGO') {
            // Check for GO/NO-GO decision
            const detailGoNoGo = mostAdvancedStep.detailGoNoGo || mostAdvancedStep.detail_go_no_go;
            if (detailGoNoGo && detailGoNoGo.decision) {
              if (detailGoNoGo.decision === 'GO') {
                distribution['GO_NOGO_GO']++;
              } else if (detailGoNoGo.decision === 'NO_GO') {
                distribution['GO_NOGO_NO_GO']++;
              } else {
                // GO_NOGO step without decision, count as regular GO_NOGO
                distribution['CANDIDATE_INTERVIEW']++; // Keep in interview stage
              }
            } else {
              // GO_NOGO step without decision, count as regular GO_NOGO
              distribution['CANDIDATE_INTERVIEW']++; // Keep in interview stage
            }
          } else if (distribution.hasOwnProperty(stepType)) {
            distribution[stepType]++;
          }
        } else {
          // No steps found, count as identified
          distribution['CANDIDATE_IDENTIFIED']++;
        }
      }
    });

    return distribution;
  }, [
    need?.id, 
    candidates, 
    getStepsForCandidate, 
    getMostAdvancedStep, 
    stepsVersion
    // ✅ FIXED: Removed the dynamic array that was causing the React warning
    // The stepsVersion will trigger recomputation when steps change
  ]);

  // ✅ NEW: Get total candidates count
  const totalCandidates = useMemo(() => {
    return Object.values(candidateStepDistribution).reduce((sum, count) => sum + count, 0);
  }, [candidateStepDistribution]);

  // Helper function for priority colors
  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 'P1':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'P2':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'P3':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'P4':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }, []);

  // Status color helper
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'no-candidate':
        return 'bg-gray-100 text-gray-800';
      case 'candidate-identified':
        return 'bg-blue-100 text-blue-800';
      case 'candidate-sent':
        return 'bg-purple-100 text-purple-800';
      case 'candidate-interview':
        return 'bg-yellow-100 text-yellow-800';
      case 'go-nogo':
        return 'bg-orange-100 text-orange-800';
      case 'sold':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  // ✅ ADDED: Listen to need changes from props (store updates)
  useEffect(() => {
    setNeedData(need);
  }, [need]);

  // Use needData instead of need for display
  const displayNeed = needData || need;

  // Process JD analysis data
  const jdAnalysisData = useMemo(() => {
    const hasJDAnalysis = need.jdAnalysisStatus === 'READY' && (need.jdAnalysis || need.jdSeniority);
    
    if (!hasJDAnalysis) {
      return {
        hasData: false,
        summary: 'JD analysis pending'
      };
    }

    const seniority = need.jdSeniority || need.jdAnalysis?.seniority;
    const team = need.jdTeam || need.jdAnalysis?.team;
    const company = need.jdCompany || need.jdAnalysis?.company;
    const location = need.jdLocation || need.jdAnalysis?.location;
    const experienceYears = need.jdExperienceYears || need.jdAnalysis?.experienceyears;
    const education = need.jdEducation || need.jdAnalysis?.education;
    const projectName = need.jdProjectName || need.jdAnalysis?.projectname;

    return {
      hasData: true,
      seniority,
      team,
      company,
      location,
      experienceYears,
      education,
      projectName,
      requiredSkills: need.jdRequiredSkills || need.jdAnalysis?.requiredskills || [],
      desiredSkills: need.jdDesiredSkills || need.jdAnalysis?.desiredskills || [],
      softSkills: need.jdSoftSkills || need.jdAnalysis?.soft_skills || [],
      responsibilities: need.jdResponsibilities || need.jdAnalysis?.responsibilities || [],
      summary: `${seniority || 'Experience TBD'} • ${team || 'Team size TBD'}`
    };
  }, [need]); // ✅ FIX: Depend on entire need object but memoized

  // Get consultants for this need
  const assignedConsultants = useMemo(() => {
    return consultants.filter(c => need.consultants?.includes(c.id)) || [];
  }, [consultants, need.consultants, need.id]);

  // first consultant considered as sales owner
  const owner = assignedConsultants[0] || null;

  // ✅ FIX: Memoize handlers to prevent unnecessary re-renders
  const handleCardClick = useCallback((e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(need, 'view');
    }
  }, [need, onClick]);

  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(need, 'edit');
    }
  }, [need, onClick]);

  const handleExpandToggle = useCallback((e) => {
    e.stopPropagation();
    setIsMinimized(prev => !prev);
  }, []);

  // Calculate urgency score
  const urgencyScore = useMemo(() => {
    let score = 0;
    if (need.priority === 'P1') score += 40;
    else if (need.priority === 'P2') score += 30;
    else if (need.priority === 'P3') score += 20;
    else score += 10;

    // Add points based on time since creation
    if (need.createdAt) {
      const daysSinceCreation = Math.floor((Date.now() - new Date(need.createdAt).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceCreation > 30) score += 30;
      else if (daysSinceCreation > 14) score += 20;
      else if (daysSinceCreation > 7) score += 10;
    }

    // Subtract points if candidates are present
    if (candidates.length > 0) score -= 20;
    if (candidates.length > 2) score -= 10;

    return Math.max(0, Math.min(100, score));
  }, [need.priority, need.createdAt, candidates.length]);

  // Retry candidates fetch
  const retryFetch = useCallback(() => {
    setCandidatesError(null);
    lastFetchedNeedId.current = null;
  }, []);

  // ✅ NEW: WebSocket handler for step updates
  const handleWebSocketMessage = useCallback((message) => {
    try {
      console.log('🔌 KanbanCard - WebSocket message received:', message);
      
      switch (message.event_type) {
        case 'step_created':
        case 'step_updated':
        case 'step_deleted': {
          const payload = message.payload || message.data || {};
          const payloadNeedId = payload.needId || payload.need_id;
          const payloadCandidateId = payload.candidateId || payload.candidate_id;

          // Determine if the event concerns this need via needId or candidate membership
          const isForThisNeed = payloadNeedId && need?.id && payloadNeedId === need.id;
          const candidatesInNeed = getCandidatesForNeed(need?.id);
          const candidateIdsInNeed = candidatesInNeed.map(c => c.id);
          const isCandidateInThisNeed = payloadCandidateId && candidateIdsInNeed.includes(payloadCandidateId);

          if (isForThisNeed || isCandidateInThisNeed) {
            console.log('🔌 KanbanCard - Step change relevant to this card, refreshing step data');

            // If we have a specific candidate, refresh only that one; else refresh all in this need
            const targets = payloadCandidateId
              ? candidatesInNeed.filter(c => c.id === payloadCandidateId)
              : candidatesInNeed;

            targets.forEach(candidate => {
              if (candidate?.id) {
                loadStepsForCandidate(need.id, candidate.id)
                  .catch(error => {
                    console.error('🔌 KanbanCard - Error refreshing steps for candidate:', candidate.id, error);
                  });
              }
            });

            // Bump local version to trigger recompute
            setTimeout(() => setStepsVersion(v => v + 1), 150);
          }
          break;
        }
          
        case 'candidate_updated':
          // If a candidate in this need was updated, refresh candidates
          if (message.payload?.need_id === need?.id || message.payload?.needId === need?.id) {
            console.log('🔌 KanbanCard - Candidate updated for this need');
            // The store will handle candidate updates automatically
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error('🔌 KanbanCard - Error handling WebSocket message:', error);
    }
  }, [need?.id, getCandidatesForNeed, loadStepsForCandidate]);

  // WebSocket subscription handled globally in store to avoid duplicates

  // ✅ ENHANCED: Render candidate step distribution with better UX/UI
  const renderCandidateStepDistribution = () => {
    if (totalCandidates === 0) {
      return (
        <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
            <User className="w-3 h-3 text-gray-400" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-600">No candidates</p>
            <p className="text-xs text-gray-400">Add candidates to track progress</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {Object.entries(candidateStepDistribution).map(([stepType, count]) => {
          if (count === 0) return null;
          
          const config = stepConfig[stepType];
          return (
            <div
              key={stepType}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-all duration-200 hover:shadow-md hover:scale-105 cursor-pointer ${config.bgColor} ${config.color.replace('text-', 'text-').replace('border-', 'border-')}`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${config.color}`}>
                {config.label}
              </div>
              <span className="text-xs font-semibold">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ✅ FIX: Remove excessive logging in candidate rendering
  const renderCandidates = () => {
    if (displayCandidates.length === 0) {
      return (
        <div className="text-center py-3">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <User className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-xs text-gray-500">No candidates yet</p>
          <p className="text-xs text-gray-400">Click to add candidates</p>
        </div>
      );
    }

    // Loading skeleton - removed since we're using store data
    if (fetchingRef.current) {
      return (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-2 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    const candidatesToShow = showCandidatesDetail ? displayCandidates : displayCandidates.slice(0, 2);

    return (
      <div className="space-y-2">
        {candidatesToShow.map((candidate, index) => {
          // ✅ FIX: Better candidate name extraction without excessive logging
          const candidateName = candidate.name || 
                               `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 
                               candidate.fullName || 
                               'Unknown Candidate';
          
          const candidateInitial = candidateName.charAt(0).toUpperCase() || '?';
          
          // ✅ FIX: Get candidate's most advanced step instead of latest by date
          const candidateSteps = getStepsForCandidate(need.id, candidate.id);
          // ✅ PHASE 2: Pass candidateId for caching
          const mostAdvancedStep = getMostAdvancedStep(candidateSteps, candidate.id);
          const stepType = mostAdvancedStep?.type || 'CANDIDATE_IDENTIFIED';
          
          // ✅ NEW: Handle GO/NO-GO decision
          let displayStepType = stepType;
          if (stepType === 'GO_NOGO') {
            const detailGoNoGo = mostAdvancedStep?.detailGoNoGo || mostAdvancedStep?.detail_go_no_go;
            if (detailGoNoGo && detailGoNoGo.decision) {
              if (detailGoNoGo.decision === 'GO') {
                displayStepType = 'GO_NOGO_GO';
              } else if (detailGoNoGo.decision === 'NO_GO') {
                displayStepType = 'GO_NOGO_NO_GO';
              }
            }
          }
          
          // ✅ FIX: Use a different variable name to avoid shadowing
          const candidateStepConfig = stepConfig[displayStepType] || stepConfig['CANDIDATE_IDENTIFIED'];
          
          return (
            <div key={candidate.id || `candidate-${index}`} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div 
                className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:scale-105 transition-transform flex-shrink-0"
                title={`${candidateName} - ${candidate.cvRole || candidate.cv_role || candidate.role || 'Candidate'}`}
              >
                <span className="text-xs font-bold text-white">
                  {candidateInitial}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">
                  {candidateName}
                </p>
                {(candidate.cvRole || candidate.cv_role || candidate.role) && (
                  <p className="text-xs text-gray-500 truncate mb-1">
                    {candidate.cvRole || candidate.cv_role || candidate.role}
                  </p>
                )}
                {/* ✅ FIX: Show candidate's most advanced step */}
                {mostAdvancedStep && (
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`px-1.5 py-0.5 text-xs rounded-full border ${candidateStepConfig.color}`}>
                      {candidateStepConfig.label}
                    </span>
                  </div>
                )}
                {(candidate.skills || candidate.cvSkills || candidate.hardSkills) && (
                  <div className="flex items-center gap-1 mt-1">
                    <div className="flex gap-1 flex-wrap">
                      {(candidate.skills || candidate.cvSkills || candidate.hardSkills || []).slice(0, 2).map((skill, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded whitespace-nowrap">
                          {typeof skill === 'string' ? skill : skill.name || skill.skill || 'Skill'}
                        </span>
                      ))}
                      {(candidate.skills || candidate.cvSkills || candidate.hardSkills || []).length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded whitespace-nowrap">
                          +{(candidate.skills || candidate.cvSkills || candidate.hardSkills || []).length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0 mt-1" title="Active candidate" />
            </div>
          );
        })}
        {displayCandidates.length > 2 && !showCandidatesDetail && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowCandidatesDetail(true); }}
            className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium py-1 hover:bg-blue-50 rounded transition-colors"
          >
            Show {displayCandidates.length - 2} more candidates
          </button>
        )}
        {showCandidatesDetail && displayCandidates.length > 2 && (
          <p className="text-xs text-gray-500 text-center py-1">
            <button
              onClick={(e) => { e.stopPropagation(); setShowCandidatesDetail(false); }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Show less
            </button>
          </p>
        )}
      </div>
    );
  };

  // Minimized view - only essential information
  if (isMinimized) {
    return (
      <div 
        className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-all duration-200 cursor-pointer group relative ${
          urgencyScore > 70 ? 'border-red-200 hover:border-red-300' :
          urgencyScore > 50 ? 'border-orange-200 hover:border-orange-300' :
          'border-gray-200 hover:border-blue-300'
        }`}
        onClick={handleCardClick}
      >
        {/* Progress Bar - Top Edge */}
        {need.progressPct !== undefined && (
          <div className="absolute top-0 left-0 w-full h-1 bg-gray-200 rounded-t-lg">
            <div 
              className={`h-full transition-all duration-500 rounded-t-lg ${
                need.progressPct > 80 ? 'bg-green-500' :
                need.progressPct > 50 ? 'bg-blue-500' :
                need.progressPct > 25 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${need.progressPct}%` }}
              title={`Progress: ${need.progressPct}%`}
            />
          </div>
        )}

        {/* Priority Badge and NEW Badge - Top Left */}
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(need.priority)}`}>
            {need.priority}
          </span>
          {isFreshNeed(need.createdAt) && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200 flex items-center gap-1">
              <Sparkles size={10} />
              NEW
            </span>
          )}
        </div>

        {/* Minimized Content */}
        <div className="p-3 pt-8">
          <div className="flex items-center gap-2 mb-1">
            {need.quantity > 1 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border border-blue-200">
                {need.quantity}x
              </span>
            )}
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-700 transition-colors leading-tight">
              {need.title}
            </h3>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
            <Building className="w-3 h-3" />
            <span className="font-medium truncate">{need.client}</span>
          </div>

          {/* Location */}
          {(need.jdLocation || need.jdAnalysis?.location) && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2 -mt-1">
              <MapPin className="w-3 h-3 text-green-400" />
              <span className="truncate">{need.jdLocation || need.jdAnalysis?.location}</span>
            </div>
          )}

          {/* ✅ ENHANCED: Candidate step distribution in minimized view */}
          {totalCandidates > 0 && (
            <div className="mb-3">
              {renderCandidateStepDistribution()}
            </div>
          )}

          {/* Candidates count indicator - REMOVED */}
          {owner && (
            <div
              className="w-7 h-7 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mt-1"
              title={`Sales: ${owner.name}`}
            >
              <span className="text-[10px] font-bold text-white leading-none">
                {getInitials(owner.name)}
              </span>
            </div>
          )}
        </div>

        {/* Expand indicator */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center" onClick={handleExpandToggle} aria-label="Expand card">
            <ChevronRight className="w-3 h-3 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // Detailed view (existing full card)
  return (
    <div 
      className={`bg-white rounded-xl shadow-sm border-2 hover:shadow-lg transition-all duration-300 cursor-pointer group relative overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-opacity-50 ${
        urgencyScore > 70 ? 'border-red-200 hover:border-red-300' :
        urgencyScore > 50 ? 'border-orange-200 hover:border-orange-300' :
        'border-gray-200 hover:border-blue-300'
      }`}
      onClick={handleCardClick}
    >
      {/* Progress Bar - Top Edge */}
      {need.progressPct !== undefined && (
        <div className="absolute top-0 left-0 w-full h-1 bg-gray-200">
          <div 
            className={`h-full transition-all duration-500 ${
              need.progressPct > 80 ? 'bg-green-500' :
              need.progressPct > 50 ? 'bg-blue-500' :
              need.progressPct > 25 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${need.progressPct}%` }}
            title={`Progress: ${need.progressPct}%`}
          />
        </div>
      )}

      {/* Priority/Status Ribbon - Top Left */}
      <div className="absolute top-3 left-0 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-r-lg shadow-sm z-10 border-r border-t border-b border-gray-200">
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(need.priority)}`}>
          {need.priority}
        </span>
      </div>

      {/* Urgency Indicator */}
      {urgencyScore > 60 && (
        <div className={`absolute top-0 right-0 w-0 h-0 border-l-[20px] border-l-transparent border-t-[20px] ${
          urgencyScore > 80 ? 'border-t-red-500' : 'border-t-orange-500'
        }`}>
          <Zap className="absolute -top-4 -right-3 w-3 h-3 text-white" />
        </div>
      )}

      {/* Kebab Menu - Top Right */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
        <button
          onClick={(e) => { e.stopPropagation(); setShowKebabMenu(!showKebabMenu); }}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/80 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="More actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {showKebabMenu && (
          <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
            <button
              onClick={(e) => { e.stopPropagation(); handleEditClick(e); setShowKebabMenu(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Edit3 className="w-3 h-3" />
              Edit
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleCardClick(e); setShowKebabMenu(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye className="w-3 h-3" />
              View Details
            </button>
          </div>
        )}
      </div>

      {/* Minimize Button - Top Right */}
      <div className="absolute top-2 right-12 opacity-0 group-hover:opacity-100 transition-opacity z-20">
        <button
          onClick={handleExpandToggle}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/80 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Minimize card"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Header Section */}
      <div className="p-4 pt-14 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              {need.quantity > 1 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border border-blue-200">
                  {need.quantity}x
                </span>
              )}
              {isFreshNeed(need.createdAt) && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200 flex items-center gap-1">
                  <Sparkles size={10} />
                  NEW
                </span>
              )}
              <h3 className="text-base font-bold text-gray-900 line-clamp-2 group-hover:text-blue-700 transition-colors leading-tight pr-8">
                {need.title}
              </h3>
            </div>
            
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Building className="w-3 h-3" />
                <span className="font-medium">{need.client}</span>
              </div>
              {need.createdAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(need.createdAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            {owner && (
              <div className="mt-3">
                <div
                  className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center"
                  title={`Sales: ${owner.name}`}
                >
                  <span className="text-[11px] font-bold text-white leading-none">
                    {getInitials(owner.name)}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={handleExpandToggle}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* ✅ ENHANCED: Candidate step distribution in detailed view */}
        {totalCandidates > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              {Object.entries(candidateStepDistribution).map(([stepType, count]) => {
                if (count === 0) return null;
                
                const config = stepConfig[stepType];
                return (
                  <div
                    key={stepType}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 hover:shadow-md hover:scale-105 cursor-pointer ${config.bgColor} ${config.color.replace('text-', 'text-').replace('border-', 'border-')}`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${config.color}`}>
                      {config.label}
                    </div>
                    <span className="text-sm font-bold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* JD Analysis - Collapsible */}
        {jdAnalysisData.hasData && (
          <div className="mb-3">
            <button
              onClick={(e) => { e.stopPropagation(); setShowJDAnalysis(!showJDAnalysis); }}
              className="w-full flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 hover:from-blue-100 hover:to-indigo-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-800">AI Analysis Available</span>
              </div>
              {showJDAnalysis ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />}
            </button>
            
            {showJDAnalysis && (
              <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {jdAnalysisData.seniority && (
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-blue-400" />
                      <span className="text-gray-700">{jdAnalysisData.seniority}</span>
                    </div>
                  )}
                  {jdAnalysisData.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-green-400" />
                      <span className="text-gray-700 truncate">{jdAnalysisData.location}</span>
                    </div>
                  )}
                  {jdAnalysisData.team && (
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-purple-400" />
                      <span className="text-gray-700 truncate">{jdAnalysisData.team}</span>
                    </div>
                  )}
                  {jdAnalysisData.education && (
                    <div className="flex items-center gap-1 sm:col-span-2">
                      <GraduationCap className="w-3 h-3 text-orange-400" />
                      <span className="text-gray-700 truncate">{jdAnalysisData.education}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-2">
          {/* Skills Preview */}
          {jdAnalysisData.hasData && jdAnalysisData.requiredSkills.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-3 h-3 text-red-500" />
                <span className="text-xs font-medium text-gray-700">Key Skills Required</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {jdAnalysisData.requiredSkills.slice(0, 4).map((skill, index) => (
                  <span key={index} className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded border border-red-200">
                    {skill}
                  </span>
                ))}
                {jdAnalysisData.requiredSkills.length > 4 && (
                  <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded border">
                    +{jdAnalysisData.requiredSkills.length - 4}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Project Info */}
          {jdAnalysisData.projectName && (
            <div className="mb-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Briefcase className="w-3 h-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Project:</span>
                <span className="text-xs text-gray-600">{jdAnalysisData.projectName}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Candidates Section - FIXED */}
      <div className="px-4 py-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={(e) => { e.stopPropagation(); setShowCandidatesDetail(!showCandidatesDetail); }}
            className="flex items-center gap-2 hover:text-blue-600 transition-colors"
          >
            <User className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">
              Candidates
            </span>
            {showCandidatesDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {(fetchingRef.current) && (
            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        
        {candidatesError && (
          <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-red-800">Error loading candidates</span>
            </div>
            <p className="text-xs text-red-700 mb-2">{candidatesError}</p>
            <button
              onClick={(e) => { e.stopPropagation(); retryFetch(); }}
              className="flex items-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <RotateCcw className="w-3 h-3" />
              Retry
            </button>
          </div>
        )}
        
        {renderCandidates()}
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
        <div className="flex items-center justify-between">
          {/* Assigned Consultants removed to avoid duplicate badge */}
          <div />
          
          {/* Action Buttons - Hidden by default, shown on hover */}
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); if (onClick) onClick(need, 'view'); }}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="View details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={handleEditClick}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Edit need"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={handleExpandToggle}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-gray-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Minimize card"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
